import React from 'react';
import { Link } from 'react-router-dom';
import { useTheme } from '@/App';
import { ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import ThemeLanguageToggle from '@/components/ThemeLanguageToggle';

const PrivacyPolicy = () => {
  const { theme } = useTheme();
  
  return (
    <div className="min-h-screen bg-slate-50 dark:bg-[#0A0A0A]">
      {/* Header */}
      <header className="sticky top-0 backdrop-blur-xl bg-white/85 dark:bg-[#0A0A0A]/85 border-b border-slate-200 dark:border-zinc-800/80 z-20">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <Link to="/">
              <Button variant="ghost" size="sm" className="dark:text-zinc-300">
                <ArrowLeft className="w-5 h-5" />
              </Button>
            </Link>
            <Link to="/" className="flex items-center gap-2">
              <img src="/images/logo.png" alt="Nerava" className="w-8 h-8" />
              <span className="font-bold text-slate-900 dark:text-zinc-50">Nerava</span>
            </Link>
          </div>
          {/* <ThemeLanguageToggle /> */}
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-12">
        <h1 className="text-3xl font-bold text-slate-900 dark:text-zinc-50 dark:text-zinc-50 mb-2" style={{ fontFamily: 'Manrope' }}>Kebijakan Privasi</h1>
        <p className="text-slate-500 dark:text-zinc-400 mb-8">Terakhir diperbarui: 1 Januari 2024</p>

        <div className="prose prose-slate dark:prose-invert max-w-none">
          <section className="mb-8">
            <h2 className="text-xl font-semibold text-slate-900 dark:text-zinc-50 mb-4">1. Pendahuluan</h2>
            <p className="text-slate-600 dark:text-zinc-300 mb-4">
              Nerava.id ("kami", "kita", atau "Nerava") berkomitmen untuk melindungi privasi pengguna kami. 
              Kebijakan Privasi ini menjelaskan bagaimana kami mengumpulkan, menggunakan, menyimpan, dan melindungi 
              informasi pribadi Anda ketika Anda menggunakan layanan platform aiAgent AI kami.
            </p>
            <p className="text-slate-600 dark:text-zinc-300">
              Dengan menggunakan layanan Nerava, Anda menyetujui praktik yang dijelaskan dalam Kebijakan Privasi ini.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-slate-900 dark:text-zinc-50 mb-4">2. Informasi yang Kami Kumpulkan</h2>
            <h3 className="text-lg font-medium text-slate-800 mb-2 dark:text-zinc-50">2.1 Informasi yang Anda Berikan</h3>
            <ul className="list-disc list-inside text-slate-600 dark:text-zinc-300 mb-4 space-y-1">
              <li>Nama lengkap dan alamat email saat pendaftaran akun</li>
              <li>Informasi profil yang Anda pilih untuk ditambahkan</li>
              <li>Konfigurasi aiAgent (nama, deskripsi, FAQ, system prompt)</li>
              <li>Informasi pembayaran untuk langganan berbayar</li>
              <li>Komunikasi dengan tim dukungan kami</li>
            </ul>
            
            <h3 className="text-lg font-medium text-slate-800 mb-2 dark:text-zinc-50">2.2 Informasi yang Dikumpulkan Secara Otomatis</h3>
            <ul className="list-disc list-inside text-slate-600 dark:text-zinc-300 mb-4 space-y-1">
              <li>Log percakapan aiAgent untuk analitik dan peningkatan layanan</li>
              <li>Data penggunaan platform (fitur yang digunakan, frekuensi akses)</li>
              <li>Informasi perangkat dan browser</li>
              <li>Alamat IP dan lokasi umum</li>
              <li>Cookies dan teknologi pelacakan serupa</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-slate-900 dark:text-zinc-50 mb-4">3. Penggunaan Informasi</h2>
            <p className="text-slate-600 dark:text-zinc-300 mb-4">Kami menggunakan informasi yang dikumpulkan untuk:</p>
            <ul className="list-disc list-inside text-slate-600 dark:text-zinc-300 space-y-1">
              <li>Menyediakan, memelihara, dan meningkatkan layanan Nerava</li>
              <li>Memproses transaksi dan mengirim notifikasi terkait</li>
              <li>Mengirim informasi teknis, pembaruan, dan peringatan keamanan</li>
              <li>Merespons pertanyaan dan permintaan dukungan</li>
              <li>Menganalisis penggunaan untuk meningkatkan pengalaman pengguna</li>
              <li>Mendeteksi dan mencegah penipuan atau penyalahgunaan</li>
              <li>Mematuhi kewajiban hukum</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-slate-900 dark:text-zinc-50 mb-4">4. Berbagi Informasi</h2>
            <p className="text-slate-600 dark:text-zinc-300 mb-4">Kami tidak menjual informasi pribadi Anda. Kami dapat membagikan informasi dengan:</p>
            <ul className="list-disc list-inside text-slate-600 dark:text-zinc-300 space-y-1">
              <li><strong>Penyedia Layanan:</strong> Pihak ketiga yang membantu operasional kami (hosting, pembayaran, analitik)</li>
              <li><strong>Mitra Bisnis:</strong> Dengan persetujuan Anda untuk integrasi tertentu</li>
              <li><strong>Kepatuhan Hukum:</strong> Jika diwajibkan oleh hukum atau proses hukum</li>
              <li><strong>Perlindungan Hak:</strong> Untuk melindungi hak, keamanan, atau properti Nerava dan pengguna</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-slate-900 dark:text-zinc-50 mb-4">5. Keamanan Data</h2>
            <p className="text-slate-600 dark:text-zinc-300 mb-4">
              Kami menerapkan langkah-langkah keamanan teknis dan organisasi yang sesuai untuk melindungi 
              informasi pribadi Anda, termasuk:
            </p>
            <ul className="list-disc list-inside text-slate-600 dark:text-zinc-300 space-y-1">
              <li>Enkripsi data dalam transit (HTTPS/TLS)</li>
              <li>Enkripsi password dengan algoritma bcrypt</li>
              <li>Akses terbatas ke data pribadi hanya untuk karyawan yang membutuhkan</li>
              <li>Audit keamanan berkala</li>
              <li>Backup data secara teratur</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-slate-900 dark:text-zinc-50 mb-4">6. Retensi Data</h2>
            <p className="text-slate-600 dark:text-zinc-300">
              Kami menyimpan informasi pribadi Anda selama akun Anda aktif atau selama diperlukan untuk 
              menyediakan layanan. Data log percakapan aiAgent disimpan selama 12 bulan untuk keperluan 
              analitik, kecuali Anda meminta penghapusan lebih awal. Setelah penghapusan akun, data akan 
              dihapus dalam waktu 30 hari, kecuali jika diwajibkan oleh hukum untuk disimpan lebih lama.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-slate-900 dark:text-zinc-50 mb-4">7. Hak Pengguna</h2>
            <p className="text-slate-600 dark:text-zinc-300 mb-4">Anda memiliki hak untuk:</p>
            <ul className="list-disc list-inside text-slate-600 dark:text-zinc-300 space-y-1">
              <li><strong>Mengakses:</strong> Meminta salinan data pribadi Anda</li>
              <li><strong>Memperbaiki:</strong> Memperbarui informasi yang tidak akurat</li>
              <li><strong>Menghapus:</strong> Meminta penghapusan data pribadi Anda</li>
              <li><strong>Membatasi:</strong> Membatasi pemrosesan data Anda</li>
              <li><strong>Portabilitas:</strong> Menerima data Anda dalam format yang dapat dibaca mesin</li>
              <li><strong>Keberatan:</strong> Menolak pemrosesan untuk tujuan tertentu</li>
            </ul>
            <p className="text-slate-600 dark:text-zinc-300 mt-4">
              Untuk menggunakan hak-hak ini, hubungi kami di support@nerava.id
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-slate-900 dark:text-zinc-50 mb-4">8. Cookies</h2>
            <p className="text-slate-600 dark:text-zinc-300">
              Kami menggunakan cookies dan teknologi serupa untuk meningkatkan pengalaman pengguna, 
              menganalisis lalu lintas, dan untuk tujuan keamanan. Anda dapat mengontrol penggunaan 
              cookies melalui pengaturan browser Anda, namun beberapa fitur mungkin tidak berfungsi 
              dengan baik jika cookies dinonaktifkan.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-slate-900 dark:text-zinc-50 mb-4">9. Perubahan Kebijakan</h2>
            <p className="text-slate-600 dark:text-zinc-300">
              Kami dapat memperbarui Kebijakan Privasi ini dari waktu ke waktu. Perubahan material akan 
              diberitahukan melalui email atau pemberitahuan di platform kami. Penggunaan berkelanjutan 
              atas layanan kami setelah perubahan merupakan penerimaan atas kebijakan yang diperbarui.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-slate-900 dark:text-zinc-50 mb-4">10. Kontak</h2>
            <p className="text-slate-600 dark:text-zinc-300">
              Jika Anda memiliki pertanyaan tentang Kebijakan Privasi ini, silakan hubungi kami:
            </p>
            <div className="bg-slate-100 dark:bg-zinc-900 rounded-xl p-4 mt-4">
              <p className="text-slate-700 dark:text-zinc-200"><strong>Nerava.id</strong></p>
              <p className="text-slate-600 dark:text-zinc-300">Email: support@nerava.id</p>
              <p className="text-slate-600 dark:text-zinc-300">WhatsApp: 085121288598</p>
            </div>
          </section>
        </div>
      </main>
    </div>
  );
};

export default PrivacyPolicy;
